import React, { useState, useEffect } from 'react';

import API_CONFIG from '../../config/api';

interface Link {
  _id: string;
  title: string;
  url: string;
  position: 'header' | 'footer' | 'sidebar' | 'homepage' | 'dashboard';
  active: boolean;
  order: number;
  color: 'blue' | 'green' | 'purple' | 'red' | 'yellow' | 'indigo' | 'pink' | 'gray';
  icon: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

const Links: React.FC = () => {
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showLinkForm, setShowLinkForm] = useState<boolean>(false);
  const [editingLink, setEditingLink] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [newLink, setNewLink] = useState({
    title: '',
    url: '',
    position: 'dashboard' as Link['position'],
    active: true,
    order: 0,
    color: 'blue' as Link['color'],
    icon: 'link',
    description: ''
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        return;
      }

      const response = await fetch(API_CONFIG.getUrl('links'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت لینک‌ها');
      }

      const data = await response.json();
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت لینک‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (editingLink) {
      // Update the link being edited in the list
      setLinks(links.map(link => 
        link._id === editingLink 
          ? { ...link, [name]: name === 'active' ? value === 'true' : name === 'order' ? parseInt(value) : value }
          : link
      ));
    } else {
      // Update the new link
      setNewLink({
        ...newLink,
        [name]: name === 'active' ? value === 'true' : name === 'order' ? parseInt(value) : value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        return;
      }

      const linkData = editingLink 
        ? links.find(link => link._id === editingLink)
        : newLink;

      if (!linkData) {
        setError('داده‌های لینک یافت نشد');
        return;
      }

      const url = editingLink 
        ? API_CONFIG.getUrl(`links/${editingLink}`)
        : API_CONFIG.getUrl('links');
      
      const method = editingLink ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(linkData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'خطا در ذخیره لینک');
      }

      // Refresh the links list
      await fetchLinks();
      
      // Reset form
      setNewLink({
        title: '',
        url: '',
        position: 'dashboard',
        active: true,
        order: 0,
        color: 'blue',
        icon: 'link',
        description: ''
      });
      setEditingLink(null);
      setShowLinkForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره لینک');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (id: string) => {
    setEditingLink(id);
    setShowLinkForm(true);
  };

  const cancelEditing = () => {
    setEditingLink(null);
    setShowLinkForm(false);
    setNewLink({
      title: '',
      url: '',
      position: 'dashboard',
      active: true,
      order: 0,
      color: 'blue',
      icon: 'link',
      description: ''
    });
    // Refresh to reset any changes
    fetchLinks();
  };

  const toggleLinkStatus = async (id: string) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        return;
      }

      const response = await fetch(API_CONFIG.getUrl(`links/${id}/toggle`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در تغییر وضعیت لینک');
      }

      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در تغییر وضعیت لینک');
    }
  };

  const deleteLink = async (id: string) => {
    if (!window.confirm('آیا از حذف این لینک اطمینان دارید؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        return;
      }

      const response = await fetch(API_CONFIG.getUrl(`links/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('خطا در حذف لینک');
      }

      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف لینک');
    }
  };

  const getPositionLabel = (position: string) => {
    const positionMap: Record<string, string> = {

      'dashboard': 'داشبورد'
    };
    return positionMap[position] || position;
  };

  const getColorLabel = (color: string) => {
    const colorMap: Record<string, string> = {
      'blue': 'آبی',
      'green': 'سبز',
      'purple': 'بنفش',
      'red': 'قرمز',
      'yellow': 'زرد',
      'indigo': 'نیلی',
      'pink': 'صورتی',
      'gray': 'خاکستری'
    };
    return colorMap[color] || color;
  };

  const getIconLabel = (icon: string) => {
    const iconMap: Record<string, string> = {
      'link': '🔗 لینک',
      'home': '🏠 خانه',
      'info': 'ℹ️ اطلاعات',
      'help': '❓ کمک',
      'contact': '📞 تماس',
      'about': '👥 درباره ما',
      'services': '⚙️ خدمات',
      'news': '📰 اخبار',
      'support': '🆘 پشتیبانی',
      'docs': '📚 مستندات'
    };
    return iconMap[icon] || `🔗 ${icon}`;
  };

  const currentLink = editingLink 
    ? links.find(link => link._id === editingLink)
    : newLink;

  if (loading) {
    return (
     
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
   
    );
  }

  return (

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">مدیریت لینک‌ها</h1>
            <button 
              onClick={() => {
                setEditingLink(null);
                setShowLinkForm(!showLinkForm);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {showLinkForm && !editingLink ? 'انصراف' : 'افزودن لینک جدید'}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {showLinkForm && (
            <div className="mb-8 bg-gray-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">
                {editingLink ? 'ویرایش لینک' : 'افزودن لینک جدید'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">عنوان لینک *</label>
                    <input
                      type="text"
                      name="title"
                      value={currentLink?.title || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">آدرس لینک *</label>
                    <input
                      type="url"
                      name="url"
                      value={currentLink?.url || ''}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">موقعیت نمایش *</label>
                    <select
                      name="position"
                      value={currentLink?.position || 'dashboard'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
               
                      <option value="dashboard">داشبورد</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رنگ</label>
                    <select
                      name="color"
                      value={currentLink?.color || 'blue'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="blue">آبی</option>
                      <option value="green">سبز</option>
                      <option value="purple">بنفش</option>
                      <option value="red">قرمز</option>
                      <option value="yellow">زرد</option>
                      <option value="indigo">نیلی</option>
                      <option value="pink">صورتی</option>
                      <option value="gray">خاکستری</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">آیکون</label>
                    <select
                      name="icon"
                      value={currentLink?.icon || 'link'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="link">🔗 لینک</option>
                      <option value="home">🏠 خانه</option>
                      <option value="info">ℹ️ اطلاعات</option>
                      <option value="help">❓ کمک</option>
                      <option value="contact">📞 تماس</option>
                      <option value="about">👥 درباره ما</option>
                      <option value="services">⚙️ خدمات</option>
                      <option value="news">📰 اخبار</option>
                      <option value="support">🆘 پشتیبانی</option>
                      <option value="docs">📚 مستندات</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
                    <input
                      type="number"
                      name="order"
                      value={currentLink?.order || 0}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                    <select
                      name="active"
                      value={currentLink?.active ? 'true' : 'false'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="true">فعال</option>
                      <option value="false">غیرفعال</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
                  <textarea
                    name="description"
                    value={currentLink?.description || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="توضیحات اختیاری برای لینک..."
                  />
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {submitting ? 'در حال ذخیره...' : (editingLink ? 'به‌روزرسانی لینک' : 'افزودن لینک')}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">شناسه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عنوان</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">آدرس</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">موقعیت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">رنگ</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">آیکون</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ترتیب</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {links.map(link => (
                  <tr key={link._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{link._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm font-medium">{link.title}</td>
                    <td className="py-3 px-4 text-sm">
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                      >
                        {link.url}
                      </a>
                    </td>
                    <td className="py-3 px-4 text-sm">{getPositionLabel(link.position)}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${
                        link.color === 'blue' ? 'bg-blue-500' :
                        link.color === 'green' ? 'bg-green-500' :
                        link.color === 'purple' ? 'bg-purple-500' :
                        link.color === 'red' ? 'bg-red-500' :
                        link.color === 'yellow' ? 'bg-yellow-500' :
                        link.color === 'indigo' ? 'bg-indigo-500' :
                        link.color === 'pink' ? 'bg-pink-500' :
                        'bg-gray-500'
                      }`}>
                        {getColorLabel(link.color)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{getIconLabel(link.icon)}</td>
                    <td className="py-3 px-4 text-sm">{link.order}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        link.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {link.active ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => toggleLinkStatus(link._id)}
                          className={`ml-2 px-3 py-1 rounded text-xs ${
                            link.active 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {link.active ? 'غیرفعال' : 'فعال'}
                        </button>
                        <button 
                          onClick={() => startEditing(link._id)}
                          className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        >
                          ویرایش
                        </button>
                        <button 
                          onClick={() => deleteLink(link._id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

  );
};

export default Links;